// src/utils/money.ts
function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}
function centsToDollars(cents) {
  return cents / 100;
}
function formatCurrency(cents, options = {}) {
  const {
    currency = "USD",
    locale = "en-US",
    showCents = true
  } = options;
  const dollars = centsToDollars(cents);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  }).format(dollars);
}
function calculateTip(subtotalCents, percentage) {
  return Math.round(subtotalCents * (percentage / 100));
}
function calculateTax(subtotalCents, taxRate) {
  return Math.round(subtotalCents * (taxRate / 100));
}
function calculatePercentage(partCents, totalCents) {
  if (totalCents === 0) return 0;
  return partCents / totalCents * 100;
}
function splitEvenly(totalCents, ways) {
  if (ways <= 0) return [];
  if (ways === 1) return [totalCents];
  const baseAmount = Math.floor(totalCents / ways);
  const remainder = totalCents % ways;
  const splits = [];
  for (let i = 0; i < ways; i++) {
    splits.push(baseAmount + (i < remainder ? 1 : 0));
  }
  return splits;
}
function sumCents(amounts) {
  return amounts.reduce((sum, amount) => sum + amount, 0);
}
function calculateMargin(revenueCents, costCents) {
  if (revenueCents === 0) return 0;
  return (revenueCents - costCents) / revenueCents * 100;
}
function calculateFoodCostPercent(costCents, revenueCents) {
  if (revenueCents === 0) return 0;
  return costCents / revenueCents * 100;
}
function roundToNearestDenomination(cents, denomination) {
  return Math.round(cents / denomination) * denomination;
}
function calculateChange(changeCents) {
  let remaining = changeCents;
  const twenties = Math.floor(remaining / 2e3);
  remaining %= 2e3;
  const tens = Math.floor(remaining / 1e3);
  remaining %= 1e3;
  const fives = Math.floor(remaining / 500);
  remaining %= 500;
  const ones = Math.floor(remaining / 100);
  remaining %= 100;
  const quarters = Math.floor(remaining / 25);
  remaining %= 25;
  const dimes = Math.floor(remaining / 10);
  remaining %= 10;
  const nickels = Math.floor(remaining / 5);
  remaining %= 5;
  const pennies = remaining;
  return {
    twenties,
    tens,
    fives,
    ones,
    quarters,
    dimes,
    nickels,
    pennies
  };
}

// src/utils/time.ts
function formatTime(date, options = {}) {
  const { use24Hour = false } = options;
  if (use24Hour) {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  }
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
}
function formatDuration(minutes) {
  if (minutes < 1) return "Less than a minute";
  if (minutes === 1) return "1 minute";
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  const hourStr = hours === 1 ? "1 hour" : `${hours} hours`;
  const minStr = mins === 1 ? "1 minute" : `${mins} minutes`;
  return `${hourStr} ${minStr}`;
}
function formatTicketTime(minutes) {
  const mins = Math.floor(minutes);
  const secs = Math.round((minutes - mins) * 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
function minutesSince(date) {
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  return Math.floor(diffMs / 6e4);
}
function secondsSince(date) {
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  return Math.floor(diffMs / 1e3);
}
function isToday(date) {
  const today = /* @__PURE__ */ new Date();
  return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
}
function isYesterday(date) {
  const yesterday = /* @__PURE__ */ new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear();
}
function startOfDay(date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}
function endOfDay(date) {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}
function startOfWeek(date) {
  const start = new Date(date);
  const day = start.getDay();
  start.setDate(start.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
}
function getBusinessDay(date, dayEndHour = 4) {
  const businessDay = new Date(date);
  if (businessDay.getHours() < dayEndHour) {
    businessDay.setDate(businessDay.getDate() - 1);
  }
  businessDay.setHours(0, 0, 0, 0);
  return businessDay;
}
function parseTimeString(timeStr, baseDate) {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = baseDate ? new Date(baseDate) : /* @__PURE__ */ new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}
function isWithinHours(openTime, closeTime, currentTime) {
  const now = currentTime || /* @__PURE__ */ new Date();
  const open = parseTimeString(openTime, now);
  let close = parseTimeString(closeTime, now);
  if (close <= open) {
    close.setDate(close.getDate() + 1);
    if (now < open) {
      return now <= close;
    }
  }
  return now >= open && now <= close;
}
function getRelativeTime(date) {
  const seconds = secondsSince(date);
  if (seconds < 60) return "just now";
  if (seconds < 120) return "1 minute ago";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 7200) return "1 hour ago";
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 172800) return "yesterday";
  return `${Math.floor(seconds / 86400)} days ago`;
}
function getDayName(dayIndex, short = false) {
  const days = short ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  return days[dayIndex];
}
function formatDate(date, format = "short") {
  if (format === "short") {
    return date.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric"
    });
  }
  if (format === "long") {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
  }
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}
function estimateWaitTime(queuePosition, averageTurnTimeMinutes, availableTables) {
  if (availableTables >= queuePosition) return 0;
  const turnsNeeded = Math.ceil((queuePosition - availableTables) / availableTables);
  return turnsNeeded * averageTurnTimeMinutes;
}

// src/utils/ids.ts
function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function generateShortId(length = 8) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
function generateDailyNumber(prefix, sequence) {
  return `${prefix}${sequence.toString().padStart(4, "0")}`;
}
function generatePin(length = 4) {
  let pin = "";
  for (let i = 0; i < length; i++) {
    pin += Math.floor(Math.random() * 10).toString();
  }
  return pin;
}
function generateGiftCardCode() {
  const segments = [];
  for (let i = 0; i < 4; i++) {
    segments.push(generateShortId(4));
  }
  return segments.join("-");
}
function generatePromoCode(prefix = "") {
  const code = generateShortId(6);
  return prefix ? `${prefix.toUpperCase()}${code}` : code;
}
function generateOrderNumber(restaurantPrefix, dailySequence) {
  const date = /* @__PURE__ */ new Date();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const seq = dailySequence.toString().padStart(3, "0");
  return `${restaurantPrefix}${month}${day}-${seq}`;
}
function isValidUUID(str) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}
function generateSlug(name) {
  return name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").substring(0, 50);
}
function generateUniqueSlug(name, existingSlugs) {
  const baseSlug = generateSlug(name);
  if (!existingSlugs.includes(baseSlug)) {
    return baseSlug;
  }
  let counter = 1;
  let uniqueSlug = `${baseSlug}-${counter}`;
  while (existingSlugs.includes(uniqueSlug)) {
    counter++;
    uniqueSlug = `${baseSlug}-${counter}`;
  }
  return uniqueSlug;
}

// src/utils/errors.ts
function getErrorMessage(error, fallback = "An unexpected error occurred") {
  if (!error) return fallback;
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  if (typeof error === "object") {
    const err = error;
    if (err.error && typeof err.error === "object") {
      const apiError = err.error;
      if (typeof apiError.message === "string") {
        return apiError.message;
      }
    }
    if (err.response && typeof err.response === "object") {
      const response = err.response;
      if (response.data && typeof response.data === "object") {
        const data = response.data;
        if (data.error && typeof data.error === "object") {
          const apiError = data.error;
          if (typeof apiError.message === "string") {
            return apiError.message;
          }
        }
        if (typeof data.message === "string") {
          return data.message;
        }
      }
    }
    if (typeof err.message === "string") {
      return err.message;
    }
    if (typeof err.code === "string") {
      return err.code;
    }
  }
  return fallback;
}
function getErrorCode(error) {
  if (!error || typeof error !== "object") return null;
  const err = error;
  if (err.error && typeof err.error === "object") {
    const apiError = err.error;
    if (typeof apiError.code === "string") {
      return apiError.code;
    }
  }
  if (err.response && typeof err.response === "object") {
    const response = err.response;
    if (response.data && typeof response.data === "object") {
      const data = response.data;
      if (data.error && typeof data.error === "object") {
        const apiError = data.error;
        if (typeof apiError.code === "string") {
          return apiError.code;
        }
      }
    }
  }
  if (typeof err.code === "string") {
    return err.code;
  }
  return null;
}
function isErrorCode(error, code) {
  return getErrorCode(error) === code;
}
function isNetworkError(error) {
  if (!error || typeof error !== "object") return false;
  const err = error;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNREFUSED") {
    return true;
  }
  if (error instanceof TypeError && err.message === "Failed to fetch") {
    return true;
  }
  return false;
}
function isAuthError(error) {
  if (!error || typeof error !== "object") return false;
  const err = error;
  if (err.response && typeof err.response === "object") {
    const response = err.response;
    if (response.status === 401) {
      return true;
    }
  }
  const code = getErrorCode(error);
  return code === "UNAUTHORIZED" || code === "TOKEN_EXPIRED" || code === "INVALID_TOKEN";
}

// src/constants/index.ts
var PAYMENT_PROCESSING = {
  // Processing fees by tier
  FEES: {
    starter: { percentage: 2.6, fixed: 10 },
    // 2.6% + $0.10
    essentials: { percentage: 2.4, fixed: 10 },
    professional: { percentage: 2.2, fixed: 10 },
    enterprise: { percentage: 2, fixed: 10 }
  },
  // Instant payout fee (cents)
  INSTANT_PAYOUT_FEE: 50,
  // $0.50
  // Minimum transaction amount (cents)
  MIN_TRANSACTION: 100,
  // $1.00
  // Maximum offline transaction (cents)
  MAX_OFFLINE_TRANSACTION: 2e4,
  // $200.00
  // Tip suggestions
  DEFAULT_TIP_PERCENTAGES: [18, 20, 22, 25],
  // Auto-gratuity defaults
  DEFAULT_AUTO_GRATUITY_THRESHOLD: 6,
  DEFAULT_AUTO_GRATUITY_PERCENT: 20
};
var KITCHEN = {
  // Target ticket times (minutes) by restaurant type
  TARGET_TIMES: {
    quick_service: 5,
    fast_casual: 10,
    full_service: 14,
    fine_dining: 20,
    bar: 8
  },
  // Alert thresholds
  LONG_TICKET_WARNING_PERCENT: 120,
  // 120% of target = warning
  LONG_TICKET_CRITICAL_PERCENT: 150,
  // 150% of target = critical
  // Station codes
  STATIONS: [
    "grill",
    "saute",
    "fry",
    "cold",
    "dessert",
    "bar",
    "expo",
    "pizza",
    "salad",
    "prep"
  ],
  // Course names
  DEFAULT_COURSES: [
    "Appetizers",
    "Salads",
    "Mains",
    "Desserts"
  ],
  // Rush priorities
  PRIORITY_LEVELS: {
    normal: 0,
    rush: 1,
    vip: 2,
    remake: 3,
    fire: 4
  }
};
var TABLES = {
  // Status colors
  STATUS_COLORS: {
    available: "#22c55e",
    // green
    seated: "#3b82f6",
    // blue
    occupied: "#f59e0b",
    // yellow
    check_dropped: "#a855f7",
    // purple
    needs_cleaning: "#6b7280",
    // gray
    reserved: "#06b6d4",
    // cyan
    blocked: "#ef4444"
    // red
  },
  // Turn time thresholds (minutes)
  TURN_TIME_THRESHOLDS: {
    quick_service: { expected: 20, warning: 30 },
    fast_casual: { expected: 30, warning: 45 },
    full_service: { expected: 60, warning: 90 },
    fine_dining: { expected: 90, warning: 120 },
    bar: { expected: 45, warning: 70 }
  },
  // Default table dimensions (grid units)
  DEFAULT_DIMENSIONS: {
    "2-top": { width: 1, height: 1 },
    "4-top": { width: 2, height: 1 },
    "6-top": { width: 2, height: 2 },
    "bar_seat": { width: 1, height: 1 },
    "booth_4": { width: 2, height: 1 },
    "booth_6": { width: 3, height: 1 }
  }
};
var INVENTORY = {
  // Low stock threshold (percentage of par)
  LOW_STOCK_THRESHOLD: 25,
  // Critical stock threshold
  CRITICAL_STOCK_THRESHOLD: 10,
  // Waste percentage targets
  WASTE_TARGETS: {
    excellent: 2,
    good: 4,
    acceptable: 6,
    poor: 10
  },
  // Food cost targets by restaurant type
  FOOD_COST_TARGETS: {
    quick_service: { target: 28, max: 32 },
    fast_casual: { target: 30, max: 35 },
    full_service: { target: 32, max: 38 },
    fine_dining: { target: 35, max: 42 },
    bar: { target: 22, max: 28 }
  },
  // Common units
  UNITS: [
    "each",
    "oz",
    "lb",
    "g",
    "kg",
    "ml",
    "l",
    "fl_oz",
    "cup",
    "tbsp",
    "tsp",
    "gal",
    "case",
    "bag",
    "box",
    "bottle"
  ],
  // Categories (display order)
  CATEGORIES: [
    "proteins",
    "produce",
    "dairy",
    "dry_goods",
    "frozen",
    "beverages",
    "alcohol",
    "paper_goods",
    "cleaning",
    "smallwares",
    "miscellaneous"
  ],
  // Category display names
  CATEGORY_LABELS: {
    proteins: "Proteins",
    produce: "Produce",
    dairy: "Dairy",
    dry_goods: "Dry Goods",
    frozen: "Frozen",
    beverages: "Beverages",
    alcohol: "Alcohol",
    paper_goods: "Paper Goods",
    cleaning: "Cleaning",
    smallwares: "Smallwares",
    miscellaneous: "Miscellaneous"
  }
};
var LABOR = {
  // Labor cost targets by restaurant type
  LABOR_COST_TARGETS: {
    quick_service: { target: 25, max: 30 },
    fast_casual: { target: 28, max: 33 },
    full_service: { target: 32, max: 38 },
    fine_dining: { target: 35, max: 42 },
    bar: { target: 22, max: 28 }
  },
  // Prime cost target (food + labor)
  PRIME_COST_TARGET: 60,
  PRIME_COST_MAX: 65,
  // Overtime rules
  OVERTIME_DAILY_THRESHOLD: 8,
  // Hours before daily OT
  OVERTIME_WEEKLY_THRESHOLD: 40,
  // Hours before weekly OT
  OVERTIME_RATE: 1.5,
  DOUBLE_TIME_THRESHOLD: 12,
  DOUBLE_TIME_RATE: 2,
  // Break requirements (California-style)
  BREAKS: {
    MEAL_BREAK_AFTER_HOURS: 5,
    MEAL_BREAK_DURATION: 30,
    REST_BREAK_PER_HOURS: 4,
    REST_BREAK_DURATION: 10
  },
  // Roles
  FRONT_OF_HOUSE_ROLES: [
    "server",
    "bartender",
    "host",
    "busser",
    "food_runner",
    "barback",
    "cashier"
  ],
  BACK_OF_HOUSE_ROLES: [
    "line_cook",
    "prep_cook",
    "sous_chef",
    "head_chef",
    "expeditor",
    "dishwasher"
  ],
  MANAGEMENT_ROLES: [
    "owner",
    "manager",
    "assistant_manager"
  ]
};
var LOYALTY = {
  // Default tier thresholds
  DEFAULT_TIERS: {
    bronze: { min: 0, multiplier: 1 },
    silver: { min: 500, multiplier: 1.25 },
    gold: { min: 1e3, multiplier: 1.5 },
    platinum: { min: 2500, multiplier: 2 }
  },
  // Default earning rate
  POINTS_PER_DOLLAR: 1,
  // Default redemption rate
  POINTS_PER_DOLLAR_REDEMPTION: 100,
  // 100 points = $1
  // Point expiration
  POINTS_EXPIRATION_DAYS: 365
};
var AI = {
  // Prediction confidence thresholds
  CONFIDENCE: {
    HIGH: 85,
    MEDIUM: 70,
    LOW: 50
  },
  // Alert thresholds
  ALERTS: {
    SERVICE_SLOW_MINUTES: 8,
    KITCHEN_BACKUP_TICKETS: 5,
    TABLE_DWELL_MINUTES: 90,
    CHECK_PENDING_MINUTES: 10
  },
  // Suggestion frequency limits
  SUGGESTION_COOLDOWN_MINUTES: 15,
  // Model update frequency
  MODEL_RETRAIN_DAYS: 7
};
var SYSTEM = {
  // Pagination
  DEFAULT_PAGE_SIZE: 25,
  MAX_PAGE_SIZE: 100,
  // Cache TTL (seconds)
  CACHE_TTL: {
    MENU: 300,
    // 5 minutes
    SETTINGS: 60,
    // 1 minute
    ANALYTICS: 30,
    // 30 seconds
    PREDICTIONS: 900
    // 15 minutes
  },
  // WebSocket
  WS_HEARTBEAT_INTERVAL: 3e4,
  // 30 seconds
  WS_RECONNECT_INTERVAL: 5e3,
  // 5 seconds
  // Session
  SESSION_TIMEOUT_MINUTES: 480,
  // 8 hours
  REFRESH_TOKEN_DAYS: 30,
  // Rate limits
  RATE_LIMITS: {
    API_REQUESTS_PER_MINUTE: 1e3,
    LOGIN_ATTEMPTS_PER_HOUR: 10,
    PASSWORD_RESET_PER_HOUR: 3
  }
};
var SUBSCRIPTION = {
  TIERS: {
    starter: {
      name: "Starter",
      monthlyPrice: 2e4,
      // $200
      processingRate: 2.6,
      maxTerminals: 1,
      features: ["pos", "kds", "basic_inventory", "staff", "email_support"]
    },
    essentials: {
      name: "Essentials",
      monthlyPrice: 5e4,
      // $500
      processingRate: 2.4,
      maxTerminals: 3,
      features: ["pos", "kds", "inventory", "staff", "payroll", "analytics", "priority_support"]
    },
    professional: {
      name: "Professional",
      monthlyPrice: 1e5,
      // $1000
      processingRate: 2.2,
      maxTerminals: -1,
      // Unlimited
      features: [
        "pos",
        "kds",
        "inventory",
        "staff",
        "payroll",
        "analytics",
        "multi_location",
        "api_access",
        "dedicated_support"
      ]
    },
    enterprise: {
      name: "Enterprise",
      monthlyPrice: -1,
      // Custom
      processingRate: 2,
      maxTerminals: -1,
      features: [
        "pos",
        "kds",
        "inventory",
        "staff",
        "reporting",
        "online_ordering",
        "loyalty",
        "ai_full",
        "multi_location",
        "api_access",
        "dedicated_support"
      ]
    }
  }
};
var ALLERGENS = {
  LIST: [
    { id: "gluten", name: "Gluten", icon: "\u{1F33E}" },
    { id: "dairy", name: "Dairy", icon: "\u{1F95B}" },
    { id: "eggs", name: "Eggs", icon: "\u{1F95A}" },
    { id: "fish", name: "Fish", icon: "\u{1F41F}" },
    { id: "shellfish", name: "Shellfish", icon: "\u{1F990}" },
    { id: "tree_nuts", name: "Tree Nuts", icon: "\u{1F95C}" },
    { id: "peanuts", name: "Peanuts", icon: "\u{1F95C}" },
    { id: "soy", name: "Soy", icon: "\u{1FAD8}" },
    { id: "sesame", name: "Sesame", icon: "\u{1F331}" }
  ]
};
var ERROR_CODES = {
  // Authentication
  AUTH_INVALID_CREDENTIALS: "AUTH_001",
  AUTH_TOKEN_EXPIRED: "AUTH_002",
  AUTH_INSUFFICIENT_PERMISSIONS: "AUTH_003",
  AUTH_ACCOUNT_LOCKED: "AUTH_004",
  // Payment
  PAYMENT_DECLINED: "PAY_001",
  PAYMENT_INSUFFICIENT_FUNDS: "PAY_002",
  PAYMENT_CARD_EXPIRED: "PAY_003",
  PAYMENT_PROCESSING_ERROR: "PAY_004",
  PAYMENT_REFUND_FAILED: "PAY_005",
  // Order
  ORDER_ITEM_UNAVAILABLE: "ORD_001",
  ORDER_INVALID_MODIFIER: "ORD_002",
  ORDER_CHECK_CLOSED: "ORD_003",
  ORDER_VOID_UNAUTHORIZED: "ORD_004",
  // Inventory
  INVENTORY_INSUFFICIENT_STOCK: "INV_001",
  INVENTORY_ITEM_NOT_FOUND: "INV_002",
  // Staff
  STAFF_INVALID_PIN: "STF_001",
  STAFF_ALREADY_CLOCKED_IN: "STF_002",
  STAFF_NOT_CLOCKED_IN: "STF_003",
  // System
  SYSTEM_RATE_LIMITED: "SYS_001",
  SYSTEM_MAINTENANCE: "SYS_002",
  SYSTEM_OFFLINE: "SYS_003"
};

// src/validators/index.ts
import { z } from "zod";
var emailSchema = z.string().email("Invalid email address");
var phoneSchema = z.string().regex(
  /^\+?[\d\s\-()]{10,}$/,
  "Invalid phone number"
);
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[0-9]/, "Password must contain at least one number");
var pinSchema = z.string().length(4, "PIN must be 4 digits").regex(/^\d+$/, "PIN must contain only numbers");
var slugSchema = z.string().min(1).max(50).regex(/^[a-z0-9-]+$/, "Slug must contain only lowercase letters, numbers, and hyphens");
var uuidSchema = z.string().uuid("Invalid UUID");
var positiveNumberSchema = z.number().positive("Must be a positive number");
var nonNegativeNumberSchema = z.number().min(0, "Cannot be negative");
var percentageSchema = z.number().min(0).max(100);
var currencySchema = z.number().int().min(0);
var addressSchema = z.object({
  street1: z.string().min(1, "Street address is required"),
  street2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postalCode: z.string().min(1, "Postal code is required"),
  country: z.string().default("US"),
  latitude: z.number().optional(),
  longitude: z.number().optional()
});
var loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
  deviceId: z.string().optional(),
  deviceName: z.string().optional()
});
var pinLoginSchema = z.object({
  restaurantId: uuidSchema,
  pin: pinSchema,
  deviceId: z.string().optional()
});
var registerSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: emailSchema,
  password: passwordSchema,
  phone: phoneSchema
});
var createRestaurantSchema = z.object({
  name: z.string().min(1, "Restaurant name is required").max(100),
  type: z.enum([
    "full_service",
    "fast_casual",
    "quick_service",
    "fine_dining",
    "bar",
    "cafe",
    "food_truck",
    "ghost_kitchen"
  ]),
  email: emailSchema,
  phone: phoneSchema,
  address: addressSchema,
  timezone: z.string().default("America/Los_Angeles"),
  ownerFirstName: z.string().min(1),
  ownerLastName: z.string().min(1),
  ownerEmail: emailSchema,
  ownerPhone: phoneSchema,
  ownerPassword: passwordSchema
});
var updateRestaurantSettingsSchema = z.object({
  defaultTipPercentages: z.array(percentageSchema).optional(),
  taxRate: percentageSchema.optional(),
  autoGratuityThreshold: z.number().int().min(0).optional(),
  autoGratuityPercent: percentageSchema.optional(),
  requireTableNumber: z.boolean().optional(),
  enableCoursing: z.boolean().optional(),
  enableSeatMapping: z.boolean().optional(),
  ticketPrintingEnabled: z.boolean().optional(),
  kdsEnabled: z.boolean().optional(),
  autoPacingEnabled: z.boolean().optional(),
  acceptCash: z.boolean().optional(),
  acceptCards: z.boolean().optional(),
  acceptMobilePayments: z.boolean().optional(),
  enableTipSuggestions: z.boolean().optional(),
  enableInstantPayouts: z.boolean().optional(),
  tipPoolingEnabled: z.boolean().optional(),
  onlineOrderingEnabled: z.boolean().optional(),
  qrOrderingEnabled: z.boolean().optional(),
  deliveryEnabled: z.boolean().optional(),
  lowStockAlerts: z.boolean().optional(),
  longTicketAlerts: z.boolean().optional(),
  longTicketThresholdMinutes: z.number().int().min(1).optional()
});
var createMenuItemSchema = z.object({
  categoryId: uuidSchema,
  name: z.string().min(1, "Item name is required").max(100),
  description: z.string().max(500).optional(),
  shortName: z.string().max(20).optional(),
  price: currencySchema,
  costPrice: currencySchema.optional(),
  stations: z.array(z.string()).min(1, "At least one station required"),
  prepTimeMinutes: z.number().int().min(0).default(0),
  cookTimeMinutes: z.number().int().min(0).default(0),
  tags: z.array(z.string()).optional(),
  allergens: z.array(z.string()).optional(),
  imageUrl: z.string().url().optional()
});
var updateMenuItemSchema = createMenuItemSchema.partial();
var createModifierGroupSchema = z.object({
  name: z.string().min(1).max(50),
  displayName: z.string().min(1).max(50),
  type: z.enum(["single_select", "multi_select", "quantity", "text"]),
  required: z.boolean().default(false),
  minSelections: z.number().int().min(0).default(0),
  maxSelections: z.number().int().min(1).default(1),
  modifiers: z.array(
    z.object({
      name: z.string().min(1).max(50),
      shortName: z.string().max(10).optional(),
      priceAdjustment: z.number().int().default(0),
      isDefault: z.boolean().default(false)
    })
  )
});
var createOrderItemSchema = z.object({
  menuItemId: uuidSchema,
  quantity: z.number().int().min(1).default(1),
  seatNumber: z.number().int().min(1).optional(),
  modifiers: z.array(
    z.object({
      modifierId: uuidSchema,
      quantity: z.number().int().min(1).default(1)
    })
  ).optional(),
  specialInstructions: z.string().max(200).optional(),
  courseNumber: z.number().int().min(1).optional()
});
var createOrderSchema = z.object({
  checkId: uuidSchema,
  items: z.array(createOrderItemSchema).min(1, "At least one item required"),
  courseNumber: z.number().int().min(1).optional(),
  specialInstructions: z.string().max(500).optional(),
  isRush: z.boolean().default(false),
  fireImmediately: z.boolean().default(true)
});
var voidOrderItemSchema = z.object({
  itemId: uuidSchema,
  reason: z.string().min(1, "Void reason required").max(200),
  managerPin: pinSchema.optional()
});
var openCheckSchema = z.object({
  tableId: uuidSchema.optional(),
  partySize: z.number().int().min(1).default(1),
  guestId: uuidSchema.optional(),
  guestName: z.string().max(100).optional(),
  type: z.enum(["table", "bar", "takeout", "delivery", "catering", "online"])
});
var splitCheckSchema = z.object({
  checkId: uuidSchema,
  splitType: z.enum(["by_seat", "even", "custom"]),
  splits: z.array(
    z.object({
      itemIds: z.array(uuidSchema).optional(),
      amount: currencySchema.optional(),
      guestName: z.string().max(100).optional()
    })
  ).optional()
});
var applyDiscountSchema = z.object({
  checkId: uuidSchema,
  discountType: z.enum([
    "percentage",
    "fixed_amount",
    "comp",
    "employee",
    "manager",
    "loyalty",
    "promo_code"
  ]),
  value: z.number().min(0),
  reason: z.string().max(200).optional(),
  itemIds: z.array(uuidSchema).optional(),
  managerPin: pinSchema.optional()
});
var createPaymentIntentSchema = z.object({
  checkId: uuidSchema,
  splitCheckId: uuidSchema.optional(),
  amount: currencySchema,
  tipAmount: currencySchema.optional()
});
var cashPaymentSchema = z.object({
  checkId: uuidSchema,
  splitCheckId: uuidSchema.optional(),
  amountTendered: currencySchema,
  tipAmount: currencySchema.optional()
});
var refundSchema = z.object({
  paymentId: uuidSchema,
  amount: currencySchema,
  reason: z.string().min(1).max(200),
  managerPin: pinSchema.optional()
});
var createEmployeeSchema = z.object({
  firstName: z.string().min(1).max(50),
  lastName: z.string().min(1).max(50),
  email: emailSchema,
  phone: phoneSchema,
  roles: z.array(z.string()).min(1),
  primaryRole: z.string(),
  pin: pinSchema,
  payType: z.enum(["hourly", "salary", "tip_only"]),
  hourlyRate: currencySchema.optional(),
  hireDate: z.coerce.date().optional()
});
var clockInSchema = z.object({
  employeeId: uuidSchema,
  pin: pinSchema,
  shiftId: uuidSchema.optional(),
  location: z.object({
    lat: z.number(),
    lng: z.number()
  }).optional()
});
var clockOutSchema = z.object({
  employeeId: uuidSchema,
  pin: pinSchema,
  location: z.object({
    lat: z.number(),
    lng: z.number()
  }).optional(),
  declaredCashTips: currencySchema.optional()
});
var adjustInventorySchema = z.object({
  itemId: uuidSchema,
  adjustment: z.number().int(),
  reason: z.string().min(1).max(200),
  notes: z.string().max(500).optional()
});
var receiveInventorySchema = z.object({
  purchaseOrderId: uuidSchema.optional(),
  items: z.array(
    z.object({
      itemId: uuidSchema,
      quantityReceived: z.number().min(0),
      unitCost: currencySchema.optional(),
      lotNumber: z.string().optional(),
      expirationDate: z.coerce.date().optional()
    })
  ),
  invoiceNumber: z.string().optional(),
  notes: z.string().optional()
});
var createReservationSchema = z.object({
  guestName: z.string().min(1).max(100),
  guestPhone: phoneSchema,
  guestEmail: emailSchema.optional(),
  partySize: z.number().int().min(1).max(50),
  date: z.coerce.date(),
  time: z.string().regex(/^\d{2}:\d{2}$/, "Time must be in HH:MM format"),
  duration: z.number().int().min(30).default(90),
  specialRequests: z.string().max(500).optional(),
  occasion: z.string().max(100).optional()
});
function validate(schema, data) {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, errors: result.error };
}
function validateOrThrow(schema, data) {
  return schema.parse(data);
}
function formatZodErrors(error) {
  const errors = {};
  for (const issue of error.issues) {
    const path = issue.path.join(".");
    errors[path] = issue.message;
  }
  return errors;
}

// src/hooks/useWebSocket.ts
import { useState, useEffect, useRef, useCallback } from "react";
function useWebSocket(options) {
  const {
    url,
    token,
    autoReconnect = true,
    reconnectInterval = 3e3,
    maxReconnectAttempts = 10,
    onMessage,
    onConnected,
    onDisconnected,
    onError
  } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const subscribedChannels = useRef(/* @__PURE__ */ new Set());
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);
  const connect = useCallback(() => {
    if (!token || !url) {
      return;
    }
    if (socketRef.current?.readyState === WebSocket.OPEN || socketRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    setIsConnecting(true);
    setError(null);
    try {
      const wsUrl = `${url}?token=${encodeURIComponent(token)}`;
      const socket = new WebSocket(wsUrl);
      socket.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttempts.current = 0;
        for (const channel of subscribedChannels.current) {
          socket.send(JSON.stringify({ type: "subscribe", channel }));
        }
        onConnected?.();
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          onMessage?.(message);
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      };
      socket.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);
        socketRef.current = null;
        onDisconnected?.();
        if (autoReconnect && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            reconnectInterval * Math.pow(2, reconnectAttempts.current),
            3e4
            // Max 30 seconds
          );
          reconnectAttempts.current++;
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setError("Max reconnection attempts reached");
        }
      };
      socket.onerror = (event) => {
        setError("WebSocket connection error");
        onError?.(event);
      };
      socketRef.current = socket;
    } catch (e) {
      setIsConnecting(false);
      setError(e.message);
    }
  }, [url, token, autoReconnect, reconnectInterval, maxReconnectAttempts, onMessage, onConnected, onDisconnected, onError]);
  const send = useCallback((message) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn("[WS] Cannot send message: not connected");
    }
  }, []);
  const subscribe = useCallback((channel) => {
    subscribedChannels.current.add(channel);
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "subscribe", channel }));
    }
  }, []);
  const unsubscribe = useCallback((channel) => {
    subscribedChannels.current.delete(channel);
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({ type: "unsubscribe", channel }));
    }
  }, []);
  const reconnect = useCallback(() => {
    clearReconnectTimeout();
    reconnectAttempts.current = 0;
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    connect();
  }, [connect, clearReconnectTimeout]);
  useEffect(() => {
    connect();
    return () => {
      clearReconnectTimeout();
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [connect, clearReconnectTimeout]);
  return {
    isConnected,
    isConnecting,
    error,
    send,
    subscribe,
    unsubscribe,
    reconnect
  };
}
function useWebSocketEvent(wsReturn, eventType, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
}
export {
  AI,
  ALLERGENS,
  ERROR_CODES,
  INVENTORY,
  KITCHEN,
  LABOR,
  LOYALTY,
  PAYMENT_PROCESSING,
  SUBSCRIPTION,
  SYSTEM,
  TABLES,
  addressSchema,
  adjustInventorySchema,
  applyDiscountSchema,
  calculateChange,
  calculateFoodCostPercent,
  calculateMargin,
  calculatePercentage,
  calculateTax,
  calculateTip,
  cashPaymentSchema,
  centsToDollars,
  clockInSchema,
  clockOutSchema,
  createEmployeeSchema,
  createMenuItemSchema,
  createModifierGroupSchema,
  createOrderItemSchema,
  createOrderSchema,
  createPaymentIntentSchema,
  createReservationSchema,
  createRestaurantSchema,
  currencySchema,
  dollarsToCents,
  emailSchema,
  endOfDay,
  estimateWaitTime,
  formatCurrency,
  formatDate,
  formatDuration,
  formatTicketTime,
  formatTime,
  formatZodErrors,
  generateDailyNumber,
  generateGiftCardCode,
  generateId,
  generateOrderNumber,
  generatePin,
  generatePromoCode,
  generateShortId,
  generateSlug,
  generateUniqueSlug,
  getBusinessDay,
  getDayName,
  getErrorCode,
  getErrorMessage,
  getRelativeTime,
  isAuthError,
  isErrorCode,
  isNetworkError,
  isToday,
  isValidUUID,
  isWithinHours,
  isYesterday,
  loginSchema,
  minutesSince,
  nonNegativeNumberSchema,
  openCheckSchema,
  parseTimeString,
  passwordSchema,
  percentageSchema,
  phoneSchema,
  pinLoginSchema,
  pinSchema,
  positiveNumberSchema,
  receiveInventorySchema,
  refundSchema,
  registerSchema,
  roundToNearestDenomination,
  secondsSince,
  slugSchema,
  splitCheckSchema,
  splitEvenly,
  startOfDay,
  startOfWeek,
  sumCents,
  updateMenuItemSchema,
  updateRestaurantSettingsSchema,
  useWebSocket,
  useWebSocketEvent,
  uuidSchema,
  validate,
  validateOrThrow,
  voidOrderItemSchema
};
//# sourceMappingURL=index.js.map